package com.yendo.mx.repository

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.DocumentSnapshot
import com.yendo.mx.models.Producto
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.catch

data class PaginatedResult(
    val products: List<Producto>,
    val lastSnapshot: DocumentSnapshot?
)

object ProductoRepository {
    private val db = Firebase.firestore

    fun obtenerProductosPorRestaurante(idRestaurante: String): Flow<List<Producto>> {
        return db.collection("Productos")
            .where {
                all(
                    "id_negocio" equalTo idRestaurante,
                    "operacion.disponible" equalTo true
                )
            }
            .snapshots
            .map { querySnapshot ->
                querySnapshot.documents.mapNotNull { doc ->
                    try {
                        val producto = doc.data<Producto>()
                        producto.copy(id = doc.id)
                    } catch (e: Exception) {
                        println("🚨 ERROR MAPEANDO PRODUCTO ${doc.id}: ${e.message}")
                        null
                    }
                }
            }
            .catch { error ->
                println("🚨 ERROR EN REPOSITORIO DE PRODUCTOS: ${error.message}")
                emit(emptyList())
            }
    }

    fun obtenerProductosPorZona(idZona: String): Flow<List<Producto>> {
        return db.collection("Productos")
            // 🚀 SOLUCIÓN: Encadenamos las condiciones dentro del bloque
            .where {
                all(
                    "id_zona" equalTo idZona,
                    "operacion.disponible" equalTo true
                )
            }
            .snapshots
            .map { qs ->
                qs.documents.mapNotNull { doc ->
                    try {
                        doc.data<Producto>().copy(id = doc.id)
                    } catch (e: Exception) {
                        null
                    }
                }
            }
            .catch { emit(emptyList()) }
    }

    suspend fun obtenerProductosPaginados(
        idRestaurante: String,
        categoria: String,
        limit: Int,
        startAfter: DocumentSnapshot? = null
    ): PaginatedResult {
        val catLimpia = categoria.trim() // 🚀 LIMPIEZA DE ESPACIOS
        println("🚀 [YENDO_LOG DESTACADO] --- INICIO CONSULTA PAGINADA ---")
        println("🚀 [YENDO_LOG DESTACADO] Restaurante: $idRestaurante")
        println("🚀 [YENDO_LOG DESTACADO] Categoría Original: '$categoria'")
        println("🚀 [YENDO_LOG DESTACADO] Categoría Limpia: '$catLimpia'")
        println("🚀 [YENDO_LOG DESTACADO] Límite: $limit")
        
        return try {
            var query = db.collection("Productos")
                .where {
                    all(
                        "id_negocio" equalTo idRestaurante,
                        "info_producto.categoria" equalTo catLimpia,
                        "operacion.disponible" equalTo true
                    )
                }
                .orderBy("destacado", Direction.DESCENDING) // 🚀 1. Destacados primero
                .orderBy("info_producto.nombre", Direction.ASCENDING) // 🚀 2. Alfabético A-Z
                .limit(limit.toLong())

            if (startAfter != null) {
                println("🚀 [YENDO_LOG DESTACADO] Usando cursor: ${startAfter.id}")
                query = query.startAfter(startAfter)
            }

            val snapshot = query.get()
            val products = snapshot.documents.mapNotNull { doc ->
                try {
                    doc.data<Producto>().copy(id = doc.id)
                } catch (e: Exception) {
                    println("🚨 [YENDO_LOG DESTACADO] ERROR MAPEANDO PRODUCTO ${doc.id}: ${e.message}")
                    null
                }
            }
            
            println("✅ [YENDO_LOG DESTACADO] EXITO: Encontrados ${products.size} productos")
            if (products.isEmpty()) {
                println("⚠️ [YENDO_LOG DESTACADO] ALERTA: La lista está VACÍA.")
                println("ℹ️ RECUERDA: Firestore EXCLUYE documentos que NO TIENEN los campos del orderBy.")
                println("👉 Asegúrate de que TODOS los productos tengan el campo 'destacado' (aunque sea false).")
            }
            
            PaginatedResult(products, snapshot.documents.lastOrNull())
        } catch (e: Exception) {
            println("❌ [YENDO_LOG DESTACADO] ERROR CRÍTICO EN QUERY: ${e.message}")
            e.printStackTrace()
            // Esto imprimirá el link del índice si falta uno
            PaginatedResult(emptyList(), null)
        }
    }
}
